/**
 * Movie Management API Types
 * 
 * Types for the movie management endpoints
 */

/**
 * Movie Interface - Matches actual API response
 */
export interface Movie {
  contentId: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  languageId: string;
  languageName: string;
  accessLevel: 'FREE' | 'PREMIUM' | 'ENTERPRISE';
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING';
  director: string;
  runtime: number;
  /**
   * Content priority for sorting (smaller number => higher priority).
   * Backend may return null/undefined for older rows.
   */
  priority?: number | null;
  releaseDate: string;
}

/**
 * Genre with primary flag for movie details
 */
export interface MovieGenre {
  genreId: string;
  name: string;
  nameMm: string;
  iconUrl: string;
  colorCode: string;
  isPrimary: boolean;
}

/**
 * Movie Details Response - Response from get movie by ID
 */
export interface MovieDetailsResponse {
  movie: Movie;
  genres: MovieGenre[];
  primaryGenre: MovieGenre | null;
  videoVariants: VideoVariant[];
  hasReadyVideos: boolean;
  /**
   * Content-level preview images for the movie.
   * Mirrors backend PreviewImageResponse DTO.
   */
  previewImages?: PreviewImageResponse[];
}

/**
 * Movies List Query Parameters
 */
export interface MoviesQueryParams {
  page?: number;
  size?: number;
  keyword?: string;
  languageId?: string;
  status?: string;
  accessLevel?: string;
  /** Content region: "myanmar" | "international" */
  contentRegion?: string;
}

/**
 * Movies List Response Data
 */
export interface MoviesData {
  items: Movie[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Create Movie Request Payload (JSON part of form-data)
 */
export interface CreateMovieRequest {
  title: string;
  description: string;
  languageId: string;
  director: string;
  /** Total runtime in whole minutes (dashboard derives from H:M:S as seconds, then rounds). */
  runtime: number;
  releaseDate: string;
  initialGenreIds: string[];
  primaryGenreId: string;
}

/**
 * Update Movie Request Payload
 */
export interface UpdateMovieRequest {
  title?: string;
  description?: string;
  languageId?: string;
  director?: string;
  /** Total runtime in whole minutes (dashboard derives from H:M:S as seconds, then rounds). */
  runtime?: number;
  /**
   * Optional priority update.
   * When omitted (undefined) backend won't validate/change the value.
   */
  priority?: number;
  releaseDate?: string;
  initialGenreIds?: string[];
  primaryGenreId?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING';
  accessLevel?: 'FREE' | 'PREMIUM' | 'ENTERPRISE';
}

/**
 * Video Variant information for a movie
 */
export interface VideoVariant {
  fileId?: string;
  videoId?: string;
  key?: string;
  quality: string;
  videoType: string;
  status?: string;
  isDefault?: boolean;
  playbackUrl?: string;
  downloadUrl?: string;
  durationSeconds?: number;
  bitrate?: number;
  codec?: string;
  container?: string;
  width?: number;
  height?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Supported video quality options
 */
export type VideoQuality =
  | 'ULTRA_HD_4K'
  | 'FULL_HD_1080P'
  | 'HD_720P'
  | 'SD_480P'
  | 'SD_360P'
  | 'SD_240P';

/**
 * Video type options
 */
export type VideoType =
  | 'FULL_MOVIE'
  | 'TRAILER'
  | 'TEASER'
  | 'CLIP'
  | 'EXTRA';

/**
 * Multipart upload initiation request payload
 */
export interface InitiateMultipartUploadRequest {
  filename: string;
  contentType: string;
  partCount: number;
  quality: VideoQuality | string;
  videoType: VideoType | string;
  makeDefault: boolean;
}

/**
 * Multipart upload part details
 */
export interface MultipartUploadPresignedPart {
  partNumber: number;
  url: string;
  minPartSize: number;
  maxPartSize: number;
}

/**
 * Multipart upload initiation response
 */
export interface InitiateMultipartUploadResponse {
  key: string;
  uploadId: string;
  parts: MultipartUploadPresignedPart[];
}

/**
 * Completed part payload for multipart upload completion
 */
export interface CompletedMultipartPart {
  partNumber: number;
  eTag: string;
}

/**
 * Multipart upload completion request payload
 */
export interface CompleteMultipartUploadRequest {
  key: string;
  uploadId: string;
  parts: CompletedMultipartPart[];
  quality: VideoQuality | string;
  videoType: VideoType | string;
  makeDefault: boolean;
  durationSeconds?: number | null;
  bitrate?: number | null;
  codec?: string | null;
  container?: string | null;
  width?: number | null;
  height?: number | null;
}

/**
 * Uploaded part status from multipart status check
 * Mirrors backend VideoMultipartStatusResponse.UploadedPartStatus
 */
export interface UploadedPartStatus {
  partNumber: number;
  eTag: string;
  size: number;
  lastModified: string;
}

/**
 * Multipart upload status response
 * Mirrors backend VideoMultipartStatusResponse
 */
export interface VideoMultipartStatusResponse {
  bucket: string;
  key: string;
  uploadId: string;
  partCount?: number | null;
  uploadedParts: UploadedPartStatus[];
}

/**
 * Request to regenerate presigned URLs for specific parts
 * Mirrors backend VideoMultipartRegenerateRequest
 */
export interface VideoMultipartRegenerateRequest {
  key: string;
  uploadId: string;
  partNumbers: number[];
}

/**
 * Response containing regenerated presigned URLs for specific parts
 * Mirrors backend VideoMultipartPresignResponse
 */
export interface VideoMultipartPresignResponse {
  bucket: string;
  key: string;
  uploadId: string;
  parts: MultipartUploadPresignedPart[];
}

/**
 * Preview image metadata request payload.
 * Mirrors backend PreviewImageMetadataRequest DTO.
 */
export interface PreviewImageMetadataRequest {
  sortOrder?: number;
  altText?: string; // max 255 chars
}

/**
 * Preview image update request payload.
 * Alias of PreviewImageMetadataRequest for clarity.
 */
export type PreviewImageUpdateRequest = PreviewImageMetadataRequest;

/**
 * Batch preview image upload metadata payload.
 * Mirrors backend PreviewImageBatchRequest DTO.
 */
export interface PreviewImageBatchRequest {
  metadata?: PreviewImageMetadataRequest[]; // length <= 5
}

/**
 * Preview image response payload for movies / dramas.
 * Mirrors backend PreviewImageResponse DTO.
 */
export interface PreviewImageResponse {
  previewImageId: string;
  imageUrl: string;
  sortOrder?: number;
  altText?: string | null;
  uploadedAt?: string;
}

/**
 * Helper function to format date for display
 */
export const formatDateForDisplay = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Helper function to format datetime for display
 */
export const formatDateTimeForDisplay = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
